<template>
  <div v-if="isVisible" class="quick-autoload-packinglist-modal-root">
    <div class="modal-backdrop fade show" @click="close"></div>
    <div class="modal fade show" tabindex="-1" @click.self="close">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-boxes me-2 text-primary" aria-hidden="true"></i>
              {{ $t("financialSummary.stages.shipCollectionsAutoloadPackingListTitle") }}
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-3">
              {{ $t("financialSummary.stages.shipCollectionsAutoloadPackingListHint") }}
            </p>
            <div v-if="shippingName" class="small fw-semibold text-body mb-2">
              {{ shippingName }}
            </div>
            <ContentSkeleton v-if="loading" variant="table" :rows="4" :cols="4" padded />
            <div v-else-if="loadError" class="alert alert-danger mb-0">
              {{ loadError }}
            </div>
            <div v-else-if="!editableRows.length" class="alert alert-light mb-0">
              {{ $t("financialSummary.stages.shipCollectionsAutoloadPackingListEmpty") }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th scope="col" class="text-center" style="width: 5.5rem">
                      {{ $t("financialSummary.stages.shipCollectionsAutoloadPackingListColLine") }}
                    </th>
                    <th scope="col">{{ $t("financialSummary.stages.shipCollectionsEditInvoiceUnitQtyProduct") }}</th>
                    <th
                      v-for="spec in editablePackLineFieldSpecs"
                      :key="'hed-' + spec.key"
                      scope="col"
                      class="text-end"
                      style="width: 10rem"
                    >
                      {{ spec.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in editableRows" :key="row.key">
                    <td class="small text-center text-muted tabular-nums">{{ row.lineNo }}</td>
                    <td class="small">{{ row.productLabel }}</td>
                    <td
                      v-for="spec in editablePackLineFieldSpecs"
                      :key="'inp-' + row.key + '-' + spec.key"
                      class="text-end"
                    >
                      <input
                        v-model="row.drafts[spec.key]"
                        type="text"
                        inputmode="decimal"
                        class="form-control form-control-sm text-end"
                        autocomplete="off"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">
              {{ $t("cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || saving || !editableRows.length || !!loadError"
              @click="submit"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("financialSummary.stages.shipCollectionsEditInvoiceUnitQtySave") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

/** Claves lógicas → se resuelven contra el schema (`packinglistproduct`) por nombre exacto o case-insensitive. */
const EDITABLE_PACKING_PRODUCT_KEYS = ["quantity", "netweight", "grossweight"];

function deepCloneJson(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return null;
  }
}

function looksLikeMongoObjectId(s) {
  return typeof s === "string" && /^[a-f0-9]{24}$/i.test(s);
}

function displayFromProductRef(product) {
  if (product == null || product === "") return { productId: null, productLabel: "—" };
  if (typeof product === "object" && product._id != null) {
    const id = String(product._id);
    const named =
      (product.name && String(product.name).trim()) ||
      (product.label && String(product.label).trim()) ||
      (product.title && String(product.title).trim()) ||
      (product.sku && String(product.sku).trim()) ||
      "";
    return { productId: id, productLabel: named || id };
  }
  const id = String(product).trim();
  if (!id) return { productId: null, productLabel: "—" };
  return { productId: id, productLabel: id };
}

function parseQtyDraft(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = parseFloat(String(raw).replace(",", ".").trim());
  return Number.isFinite(n) ? n : NaN;
}

function extractShippingDefinition(modelDefinitions) {
  if (!modelDefinitions || typeof modelDefinitions !== "object") return null;
  const keys = Object.keys(modelDefinitions);
  const match = keys.find((k) => String(k).toLowerCase() === "shipping");
  return match ? modelDefinitions[match] : null;
}

function normalizeFieldKeyLoose(k) {
  return String(k || "")
    .toLowerCase()
    .replace(/_/g, "");
}

/** Encuentra la clave real en `fields[]` (p. ej. `gross_weight` ↔ `grossweight`). */
function resolveFieldKeyInMeta(fieldsMeta, wantedKey) {
  const list = Array.isArray(fieldsMeta) ? fieldsMeta : [];
  if (!wantedKey) return wantedKey;
  const exact = list.find((f) => f && f.key === wantedKey);
  if (exact) return exact.key;
  const wantLoose = normalizeFieldKeyLoose(wantedKey);
  const hit = list.find((f) => f && f.key && normalizeFieldKeyLoose(f.key) === wantLoose);
  return hit && hit.key ? hit.key : wantedKey;
}

function labelForFieldMeta(fieldsMeta, resolvedKey, fallbackKey) {
  const list = Array.isArray(fieldsMeta) ? fieldsMeta : [];
  const f = list.find((x) => x && x.key === resolvedKey);
  const lab = f && f.label != null ? String(f.label).trim() : "";
  if (lab) return lab;
  const fk = fallbackKey || resolvedKey;
  if (!fk) return "";
  return String(fk)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolvePackinglistSchemas(shippingDef) {
  const empty = { rowFields: [], productFields: [] };
  const fields = shippingDef && Array.isArray(shippingDef.fields) ? shippingDef.fields : [];
  const pl = fields.find((f) => f && f.key === "packinglist" && f.type === "group");
  if (!pl || !Array.isArray(pl.fields)) return empty;
  const rowFields = [];
  let productFields = [];
  for (const sf of pl.fields) {
    if (!sf || sf.isHidden) continue;
    if (sf.key === "packinglistproduct" && sf.type === "group" && sf.isArray) {
      productFields = Array.isArray(sf.fields) ? sf.fields.filter((x) => x && !x.isHidden) : [];
    } else {
      rowFields.push(sf);
    }
  }
  return { rowFields, productFields };
}

function inferFieldTypeFromSampleValue(v) {
  if (v === null || v === undefined) return "string";
  if (typeof v === "number" && Number.isFinite(v)) return "number";
  if (typeof v === "boolean") return "boolean";
  if (typeof v === "object" && !Array.isArray(v) && v._id != null) return "reference";
  return "string";
}

function inferFieldsFromDataRow(sampleRow, excludeKeys) {
  const ex = new Set(excludeKeys);
  const skip = new Set(["_id", "__v", "parent", "createdBy", "updatedBy", "createdAt", "updatedAt"]);
  const keys = Object.keys(sampleRow || {}).filter((k) => !ex.has(k) && !skip.has(k));
  return keys.sort().map((key) => ({
    key,
    type: inferFieldTypeFromSampleValue(sampleRow[key]),
    label: key,
  }));
}

function refToId(val) {
  if (val == null || val === "") return null;
  if (typeof val === "object" && val._id != null) return String(val._id);
  const s = String(val).trim();
  return s || null;
}

function sanitizeTopLevel(obj, fieldsMeta) {
  const refKeys = new Set(
    (fieldsMeta || []).filter((f) => f.type === "reference").map((f) => f.key)
  );
  const listKeys = new Set((fieldsMeta || []).filter((f) => f.type === "list").map((f) => f.key));
  const out = { ...obj };
  for (const k of Object.keys(out)) {
    if (k === "packinglistproduct") continue;
    const v = out[k];
    if (refKeys.has(k) || listKeys.has(k)) {
      out[k] = refToId(v);
      continue;
    }
    if (v && typeof v === "object" && !Array.isArray(v) && v._id != null && !(v instanceof Date)) {
      out[k] = v._id;
    }
  }
  return out;
}

export default {
  name: "QuickAutoloadPackingListModal",
  components: { ContentSkeleton },
  props: {
    isVisible: { type: Boolean, default: false },
    shippingId: { type: String, default: "" },
    shippingName: { type: String, default: "" },
    /** FOB Trading: tras guardar packing list, aplica `autoload_invoicesupplier` sin feedback al usuario. */
    silentAutoloadInvoiceSupplierAfterPackinglist: { type: Boolean, default: false },
  },
  computed: {
    /** Líneas editables: `quantity`, `netweight` y `grossweight` con etiqueta del modelo. */
    editablePackLineFieldSpecs() {
      const meta = this.productFieldsForSanitize || [];
      return EDITABLE_PACKING_PRODUCT_KEYS.map((wanted) => {
        const key = resolveFieldKeyInMeta(meta, wanted);
        const forcedQuantityLabel = this.$t(
          "financialSummary.stages.shipCollectionsAutoloadPackingListColQuantity"
        );
        return {
          key,
          label:
            wanted === "quantity"
              ? forcedQuantityLabel
              : labelForFieldMeta(meta, key, wanted),
        };
      });
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadError: "",
      baseShippingDoc: null,
      packinglistDraft: [],
      editableRows: [],
      modelDefinitions: {},
      rowFieldsForSanitize: [],
      productFieldsForSanitize: [],
    };
  },
  watch: {
    isVisible(val) {
      if (val && this.shippingId) {
        this.runLoad();
      }
      if (!val) {
        this.resetLocal();
      }
    },
    shippingId() {
      if (this.isVisible && this.shippingId) {
        this.runLoad();
      }
    },
  },
  methods: {
    pickLabelFromProductDoc(doc) {
      if (!doc || typeof doc !== "object") return null;
      const n =
        (doc.name && String(doc.name).trim()) ||
        (doc.label && String(doc.label).trim()) ||
        (doc.title && String(doc.title).trim()) ||
        (doc.sku && String(doc.sku).trim()) ||
        "";
      return n || null;
    },
    async fetchProductLabelById(productId) {
      if (!looksLikeMongoObjectId(productId)) return null;
      const candidates = ["products", "product", "Products", "Product"];
      for (const model of candidates) {
        try {
          const { data } = await wencesApi.get(
            `/api/gotrading/dynamic/${encodeURIComponent(model)}/${encodeURIComponent(productId)}`
          );
          if (data && data._id) {
            const label = this.pickLabelFromProductDoc(data);
            if (label) return label;
          }
        } catch (e) {
          const st = e.response && e.response.status;
          if (st === 404 || st === 400) continue;
        }
      }
      return null;
    },
    async enrichRowsProductNames(rows) {
      const ids = [
        ...new Set(rows.map((r) => r.productId).filter((id) => id && looksLikeMongoObjectId(String(id)))),
      ];
      const cache = Object.create(null);
      await Promise.all(
        ids.map(async (id) => {
          const sample = rows.find((r) => String(r.productId) === String(id));
          const alreadyHuman =
            sample &&
            String(sample.productLabel).trim() &&
            String(sample.productLabel) !== String(id);
          if (alreadyHuman) {
            cache[id] = String(sample.productLabel).trim();
            return;
          }
          const fetched = await this.fetchProductLabelById(String(id));
          cache[id] = fetched || String(id);
        })
      );
      rows.forEach((row) => {
        if (!row.productId) return;
        const key = String(row.productId);
        if (cache[key] != null) {
          this.$set(row, "productLabel", cache[key]);
        }
      });
    },
    resolveFieldMetaForSanitize(samplePackRow, sampleProductRow) {
      const shipDef = extractShippingDefinition(this.modelDefinitions);
      let { rowFields, productFields } = resolvePackinglistSchemas(shipDef);
      if (!rowFields.length && samplePackRow) {
        rowFields = inferFieldsFromDataRow(samplePackRow, ["packinglistproduct"]);
      }
      if (!productFields.length && sampleProductRow) {
        productFields = inferFieldsFromDataRow(sampleProductRow, []);
      }
      this.rowFieldsForSanitize = rowFields;
      this.productFieldsForSanitize = productFields;
    },
    async buildEditableRowsFromDraft() {
      const rows = [];
      const list = this.packinglistDraft || [];
      const specs = this.editablePackLineFieldSpecs;
      for (let packIdx = 0; packIdx < list.length; packIdx++) {
        const pack = list[packIdx];
        const plp = pack && Array.isArray(pack.packinglistproduct) ? pack.packinglistproduct : [];
        for (let prodIdx = 0; prodIdx < plp.length; prodIdx++) {
          const p = plp[prodIdx] || {};
          const drafts = {};
          for (const spec of specs) {
            const raw = p[spec.key];
            const draft =
              raw === null || raw === undefined || raw === "" ? "" : String(Number(raw));
            Vue.set(drafts, spec.key, draft);
          }
          const { productId, productLabel } = displayFromProductRef(p.product);
          rows.push({
            key: `pl-${packIdx}-p-${prodIdx}`,
            packIdx,
            prodIdx,
            lineNo: packIdx + 1,
            productId,
            productLabel,
            drafts,
          });
        }
      }
      this.editableRows = rows;
      await this.enrichRowsProductNames(rows);
    },
    applyPackLineDraftsToDraft() {
      const specs = this.editablePackLineFieldSpecs;
      for (const row of this.editableRows) {
        const pack = this.packinglistDraft[row.packIdx];
        if (!pack || !Array.isArray(pack.packinglistproduct)) continue;
        const p = pack.packinglistproduct[row.prodIdx];
        if (!p) continue;
        for (const spec of specs) {
          const draft =
            row.drafts && row.drafts[spec.key] != null ? String(row.drafts[spec.key]) : "";
          const n = parseQtyDraft(draft);
          if (Number.isNaN(n)) {
            throw new Error(this.$t("financialSummary.stages.shipCollectionsEditInvoiceUnitQtyInvalidNumber"));
          }
          Vue.set(p, spec.key, n === null ? null : n);
        }
      }
    },
    buildPackinglistPayload() {
      const rows = this.packinglistDraft || [];
      const rf = this.rowFieldsForSanitize || [];
      const pf = this.productFieldsForSanitize || [];
      return rows.map((row) => {
        const { packinglistproduct, ...rest } = row || {};
        const top = sanitizeTopLevel(rest, rf);
        const plist = Array.isArray(packinglistproduct) ? packinglistproduct : [];
        const cleanedProducts = plist.map((p) => sanitizeTopLevel(p || {}, pf));
        return { ...top, packinglistproduct: cleanedProducts };
      });
    },
    async ensureModelDefinitions() {
      if (this.modelDefinitions && Object.keys(this.modelDefinitions).length) return;
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      const map = {};
      if (Array.isArray(data)) {
        for (const m of data) {
          if (m && m.modelName) map[m.modelName] = m;
        }
      }
      this.modelDefinitions = map;
    },
    resetLocal() {
      this.loadError = "";
      this.baseShippingDoc = null;
      this.packinglistDraft = [];
      this.editableRows = [];
      this.rowFieldsForSanitize = [];
      this.productFieldsForSanitize = [];
    },
    async runLoad() {
      this.loading = true;
      this.loadError = "";
      this.baseShippingDoc = null;
      this.packinglistDraft = [];
      this.editableRows = [];
      try {
        await this.ensureModelDefinitions();
        const { data: ship } = await wencesApi.get(
          `/api/gotrading/dynamic/shipping/${encodeURIComponent(this.shippingId)}`
        );
        if (!ship || !ship._id) {
          this.loadError = this.$t(
            "financialSummary.stages.shipCollectionsAutoloadPackingListLoadError"
          );
          return;
        }
        this.baseShippingDoc = ship;
        const item = deepCloneJson(ship);
        if (!item) {
          this.loadError = this.$t(
            "financialSummary.stages.shipCollectionsAutoloadPackingListLoadError"
          );
          return;
        }
        // Si ya hay packing list guardado, abrirlo para edición.
        // Solo autoload cuando el shipping todavía no tiene líneas.
        const existingPackinglist =
          ship && Array.isArray(ship.packinglist) ? deepCloneJson(ship.packinglist) : null;
        const hasExistingPackinglist = Array.isArray(existingPackinglist) && existingPackinglist.length > 0;
        let rawPl = hasExistingPackinglist ? existingPackinglist : [];
        if (!hasExistingPackinglist) {
          const { data: preloadWrap } = await wencesApi.post(
            `/api/gotrading/dynamic/shipping/preload`,
            {
              action: "autoload",
              item,
            }
          );
          const merged =
            preloadWrap && preloadWrap.data != null && typeof preloadWrap.data === "object"
              ? preloadWrap.data
              : preloadWrap;
          rawPl =
            merged && merged.packinglist != null
              ? merged.packinglist
              : merged && merged.item && merged.item.packinglist != null
                ? merged.item.packinglist
                : [];
        }
        const list = Array.isArray(rawPl) ? deepCloneJson(rawPl) : [];
        this.packinglistDraft = Array.isArray(list) ? list : [];
        const samplePack = this.packinglistDraft[0];
        const sampleProd =
          samplePack && Array.isArray(samplePack.packinglistproduct)
            ? samplePack.packinglistproduct[0]
            : null;
        this.resolveFieldMetaForSanitize(samplePack, sampleProd);
        await this.buildEditableRowsFromDraft();
      } catch (e) {
        console.error("QuickAutoloadPackingListModal runLoad:", e);
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          e.message ||
          "";
        this.loadError =
          msg || this.$t("financialSummary.stages.shipCollectionsAutoloadPackingListLoadError");
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit("close");
    },
    /**
     * Persiste invoice supplier vía mismo preload que el formulario; errores solo consola (sin Swal).
     */
    async runSilentInvoiceSupplierAutoload() {
      if (!this.silentAutoloadInvoiceSupplierAfterPackinglist || !this.shippingId) return;
      try {
        const { data: ship } = await wencesApi.get(
          `/api/gotrading/dynamic/shipping/${encodeURIComponent(this.shippingId)}`
        );
        if (!ship || !ship._id) return;
        if (!Array.isArray(ship.productandpriceshipper) || ship.productandpriceshipper.length === 0) {
          return;
        }
        const { data: preloadBody } = await wencesApi.post(`/api/gotrading/dynamic/shipping/preload`, {
          action: "autoload_invoicesupplier",
          item: ship,
        });
        const merged =
          preloadBody && preloadBody.data != null && typeof preloadBody.data === "object"
            ? preloadBody.data
            : preloadBody;
        if (!merged || merged.invoicesupplier == null) return;
        const patch = {
          partialUpdate: true,
          invoicesupplier: merged.invoicesupplier,
        };
        if (merged.portofincoterm_is !== undefined && merged.portofincoterm_is !== null) {
          patch.portofincoterm_is = merged.portofincoterm_is;
        }
        if (merged.incoterm_is !== undefined && merged.incoterm_is !== null) {
          patch.incoterm_is = merged.incoterm_is;
        }
        await wencesApi.put(
          `/api/gotrading/dynamic/shipping/${encodeURIComponent(this.shippingId)}`,
          patch
        );
      } catch (e) {
        console.error("QuickAutoloadPackingListModal silent invoice supplier autoload:", e);
      }
    },
    async submit() {
      if (!this.shippingId || !this.baseShippingDoc) return;
      try {
        this.applyPackLineDraftsToDraft();
      } catch (err) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.stages.shipCollectionsEditInvoiceUnitQtyInvalidTitle"),
          text: err.message || this.$tn("error"),
        });
        return;
      }
      this.saving = true;
      try {
        const packinglist = this.buildPackinglistPayload();
        // Sin invoice_number_date: en fobtrading la fecha de factura la carga alguien a mano al facturar.
        const packingPatch = { partialUpdate: true, packinglist };
        await wencesApi.put(
          `/api/gotrading/dynamic/shipping/${encodeURIComponent(this.shippingId)}`,
          packingPatch
        );
        await this.runSilentInvoiceSupplierAutoload();
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.shipCollectionsAutoloadPackingListSaved"),
          timer: 1800,
          showConfirmButton: false,
        });
        this.$emit("saved");
        this.close();
      } catch (e) {
        console.error("QuickAutoloadPackingListModal submit:", e);
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          e.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg || this.$tn("financialSummary.stages.shipCollectionsAutoloadPackingListSaveError"),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quick-autoload-packinglist-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1055;
}
.quick-autoload-packinglist-modal-root .modal-backdrop {
  z-index: 1;
}
.quick-autoload-packinglist-modal-root .modal {
  display: block;
  position: relative;
  z-index: 2;
}
</style>
